import { useTranslation } from 'react-i18next'
import type { OrderActions } from '../../hooks/useOrderActions'
import type { DealerOrderItem } from '../../types'
import { formatPhone, formatShortDate, formatRelative, isCopiedNotSent, previewUrl } from '../../utils/format'
import { CheckIcon, CopyIcon, ExternalIcon, UndoIcon } from './icons'
import './Orders.css'

// Составные части строки заказа: используются в таблице, карточках, дашборде и боковой панели

export function DeliveryBadge({ item, withDetails = true }: { item: DealerOrderItem; withDetails?: boolean }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  if (item.deliveryStatus === 'NOT_READY') {
    return item.processingStatus === 'ERROR'
      ? <span className="dealer-badge dealer-badge--error">{t('dealer.orders.status_error')}</span>
      : <span className="dealer-badge dealer-badge--muted">{t('dealer.orders.status_not_ready')}</span>
  }
  if (item.deliveryStatus === 'NOT_SENT') {
    return <span className="dealer-badge dealer-badge--warn">{t('dealer.orders.status_not_sent')}</span>
  }
  if (item.deliveryStatus === 'SENT') {
    return (
      <span className="dealer-status">
        <span className="dealer-badge dealer-badge--info">
          {withDetails && item.sentAt
            ? t('dealer.orders.status_sent_on', { date: formatShortDate(item.sentAt, lang) })
            : t('dealer.orders.status_sent')}
        </span>
        {withDetails && <span className="dealer-status__sub">{t('dealer.orders.not_viewed_yet')}</span>}
      </span>
    )
  }
  return (
    <span className="dealer-status">
      <span className="dealer-badge dealer-badge--success">
        {withDetails
          ? t('dealer.orders.status_viewed_times', { count: item.viewCount })
          : t('dealer.orders.status_viewed')}
      </span>
      {withDetails && item.lastViewedAt && (
        <span className="dealer-status__sub" title={new Date(item.lastViewedAt).toLocaleString(lang)}>
          {t('dealer.orders.last_viewed', { time: formatRelative(item.lastViewedAt, lang) })}
        </span>
      )}
    </span>
  )
}

export function IssuePills({ item }: { item: DealerOrderItem }) {
  const { t } = useTranslation()
  // без готового отчёта замечаний ещё нет — ничего не показываем
  if (item.processingStatus !== 'READY') return null
  const { critical, minor } = item.issues ?? { critical: 0, minor: 0 }
  if (!critical && !minor) return <span className="dealer-pill dealer-pill--ok">{t('dealer.orders.issues_none')}</span>
  return (
    <span className="dealer-pills">
      {critical > 0 && <span className="dealer-pill dealer-pill--critical">{t('dealer.orders.issues_critical', { count: critical })}</span>}
      {minor > 0 && <span className="dealer-pill dealer-pill--minor">{t('dealer.orders.issues_minor', { count: minor })}</span>}
    </span>
  )
}

export function PhoneCopy({ item, actions }: { item: DealerOrderItem; actions: OrderActions }) {
  const { t } = useTranslation()
  if (!item.clientPhone) return <span className="dealer-muted">—</span>
  const copied = !!item.phoneCopiedAt
  return (
    <span className="dealer-copy-field">
      <span className="dealer-copy-field__value">{formatPhone(item.clientPhone)}</span>
      <button
        type="button"
        className={`dealer-icon-btn${copied ? ' dealer-icon-btn--done' : ''}`}
        onClick={(e) => { e.stopPropagation(); actions.copyPhone(item) }}
        title={t('dealer.orders.copy_phone')}
        aria-label={t('dealer.orders.copy_phone')}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </button>
    </span>
  )
}

export function ReportLinkActions({ item, actions }: { item: DealerOrderItem; actions: OrderActions }) {
  const { t } = useTranslation()
  if (!item.reportUrl) return <span className="dealer-muted">—</span>
  const copied = !!item.linkCopiedAt
  return (
    <span className="dealer-link-actions">
      <button
        type="button"
        className={`dealer-chip-btn${copied ? ' dealer-chip-btn--done' : ''}`}
        onClick={(e) => { e.stopPropagation(); actions.copyLink(item) }}
        title={t('dealer.orders.copy_link_hint')}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
        {t('dealer.orders.copy_link')}
      </button>
      <a
        className="dealer-icon-btn"
        href={previewUrl(item.reportUrl)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        title={t('dealer.orders.open_report_hint')}
        aria-label={t('dealer.orders.open_report')}
      >
        <ExternalIcon />
      </a>
    </span>
  )
}

export function SendAction({ item, actions, block = false }: { item: DealerOrderItem; actions: OrderActions; block?: boolean }) {
  const { t } = useTranslation()
  const busy = actions.isBusy(item.id)

  if (actions.isJustSent(item.id) && item.deliveryStatus !== 'NOT_SENT') {
    return (
      <span className={`dealer-sent-done${block ? ' dealer-sent-done--block' : ''}`}>
        <span className="dealer-sent-done__label"><CheckIcon /> {t('dealer.orders.sent_done')}</span>
        <button
          type="button"
          className="dealer-text-btn"
          disabled={busy}
          onClick={(e) => { e.stopPropagation(); actions.undoSent(item) }}
        >
          <UndoIcon /> {t('dealer.orders.undo')}
        </button>
      </span>
    )
  }

  if (item.deliveryStatus !== 'NOT_SENT') return null

  return (
    <button
      type="button"
      className={`dealer-btn dealer-btn--primary dealer-btn--sm${block ? ' dealer-btn--block' : ''}`}
      disabled={busy}
      onClick={(e) => { e.stopPropagation(); actions.markSent(item) }}
    >
      {busy ? t('dealer.orders.marking') : t('dealer.orders.mark_sent')}
    </button>
  )
}

export function CopiedHint({ item }: { item: DealerOrderItem }) {
  const { t } = useTranslation()
  if (!isCopiedNotSent(item)) return null
  return <span className="dealer-copied-hint">{t('dealer.orders.copied_not_sent')}</span>
}
